import React from "react";
import { Link } from "react-router-dom";
import { useMe } from "../api/api_hooks";
import { manualRenewal } from "../featureFlags";
import { t } from "../i18n";

const fieldLabelClass = "mt-4 block text-xs font-bold uppercase tracking-[0.25em] text-indigo-200/70";
const inputClass = "mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-indigo-100/35 focus:border-sky-300 focus:outline-none";

function TermsConsent({ checked, onChange }) {
  // The link sits inside the sentence, whose word order differs between languages.
  const [before, after] = t("I have read and agree to the {{terms}}.").split("{{terms}}");
  return (
    <label className="mt-5 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm leading-6 text-indigo-100/85">
      <input
        type="checkbox"
        required
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-4 w-4 shrink-0 accent-cyan-300"
      />
      <span>
        {before}
        <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-bold text-cyan-200 underline underline-offset-4 hover:text-white">
          {t("Terms and Conditions")}
        </Link>
        {after}
      </span>
    </label>
  );
}

function CheckoutDetailsDialog({ title, onConfirm, onClose, initialDiscountCode = "", showDiscount = false }) {
  const { data: me } = useMe();
  // A saved number is kept for renewal reminders; it may be verified and used to sign in.
  const savedMobile = me?.phone_number || "";
  const askMobile = manualRenewal && !savedMobile;
  const [mobile, setMobile] = React.useState("");
  const [discountCode, setDiscountCode] = React.useState(initialDiscountCode);
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!acceptedTerms) return;
    onConfirm({ ...(askMobile ? { mobile: mobile.trim() } : {}), discountCode: discountCode.trim() });
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-details-title"
      onClick={handleBackdropClick}
    >
      <form
        onSubmit={handleSubmit}
        className="max-h-full w-full max-w-md overflow-y-auto rounded-3xl border border-white/10 bg-slate-950/95 p-6 text-start shadow-2xl"
      >
        <h2 id="checkout-details-title" className="text-2xl font-black text-white">{title}</h2>

        {askMobile ? (
          <>
            <p className="mt-2 text-sm leading-6 text-indigo-100/70">
              {t("We send renewal reminders to this mobile number.")}
            </p>
            <label className={fieldLabelClass} htmlFor="checkout-mobile">
              {t("Mobile number")}
            </label>
            <input
              id="checkout-mobile"
              type="tel"
              inputMode="tel"
              dir="ltr"
              required
              autoFocus
              placeholder="09121234567"
              value={mobile}
              onChange={(event) => setMobile(event.target.value)}
              className={inputClass}
            />
          </>
        ) : manualRenewal ? (
          <p className="mt-2 text-sm leading-6 text-indigo-100/70">
            {t("Renewal reminders are sent to {{mobile}}.", { mobile: savedMobile })}
          </p>
        ) : null}

        {showDiscount ? (
          <>
            {!manualRenewal ? (
              <p className="mt-2 text-sm leading-6 text-indigo-100/70">
                {t("Have an invite code? Enter it for a discount on your first Gold or Venue subscription.")}
              </p>
            ) : null}
            <label className={fieldLabelClass} htmlFor="checkout-discount">
              {manualRenewal ? t("Discount or referral code (optional)") : t("Referral code (optional)")}
            </label>
            <input
              id="checkout-discount"
              type="text"
              dir="ltr"
              value={discountCode}
              onChange={(event) => setDiscountCode(event.target.value)}
              className={inputClass}
            />
          </>
        ) : null}

        <TermsConsent checked={acceptedTerms} onChange={setAcceptedTerms} />

        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            disabled={!acceptedTerms}
            className="rounded-xl bg-cyan-300 px-5 py-2.5 font-black text-slate-950 transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("Continue to payment")}
          </button>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/15 px-5 py-2.5 font-bold text-white">
            {t("Cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Asks for what a checkout needs before calling `onConfirm`: acceptance of the terms,
 * always; on Zarinpal a mobile number for renewal reminders (unless one is saved) and an
 * optional discount code; on Stripe a referral code when `referralEligible` (new Gold or
 * Venue subscriptions). `initialDiscountCode` prefills a stored referral code.
 */
export function useCheckoutDetails() {
  const [pending, setPending] = React.useState(null);

  const request = React.useCallback((title, onConfirm, { initialDiscountCode = "", referralEligible = false } = {}) => {
    setPending({ title, onConfirm, initialDiscountCode, showDiscount: manualRenewal || referralEligible });
  }, []);

  const dialog = pending ? (
    <CheckoutDetailsDialog
      title={pending.title}
      initialDiscountCode={pending.initialDiscountCode}
      showDiscount={pending.showDiscount}
      onClose={() => setPending(null)}
      onConfirm={(details) => {
        setPending(null);
        pending.onConfirm(details);
      }}
    />
  ) : null;

  return { request, dialog };
}
